import { useCallback } from 'react';
import { useTracking } from '@edreamsodigeo/frontend-core';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';
import { ThreeColumnsItineraryType } from '@components/Results/Components/ThreeColumnsItinerary/types';
import { AlternativeItineraryValuePropositionType } from 'clientState/providers/AlternativeItineraryProvider/types';
import { RESULTS_CATEGORY } from 'clientState/providers/TrackingProviders';

const ALTERNATIVE_FLIGHT_LAYER_ACTION = 'alternative_flight_layer';

export enum AlternativeFlightType {
  RyanairCountersteering = 'RC',
  BrokenFlow = 'BF',
  NonBookable = 'NB',
}

export enum PageType {
  FlightsResults = 'flights_results',
  Summary = 'summary',
  Pax = 'pax',
  Pay = 'pay',
}

export const valuePropositionMapping: Record<
  AlternativeItineraryValuePropositionType,
  string
> = {
  price: 'BP',
  discount: 'DS',
  cabinLuggage: 'SB',
  checkedLuggage: 'BB',
  airport: 'AP',
  convenience: 'TS',
  social: 'SP',
  directFlight: 'DF',
};

const useItineraryIATA = (itinerary: ThreeColumnsItineraryType | undefined) => {
  const { tripType } = usePerformedSearchRequest() || {};
  const legsSize = (itinerary?.legs.length || 1) - 1;
  const legIndex = tripType === 'MULTIPLE_DESTINATIONS' ? legsSize : 0;

  const getIATA = (index: number, segment: string) =>
    itinerary?.legs?.[index]?.segments[0]?.[segment]?.iata;

  const getAirlineCodes = () => {
    const carrierIds = new Set<string>();
    itinerary?.legs.forEach((leg) => {
      leg.segments.forEach((segment) => {
        carrierIds.add(segment.carrier.id);
      });
    });
    return Array.from(carrierIds).join('-');
  };

  return {
    departureAirportCode: getIATA(0, 'departure'),
    destinationAirportCode: getIATA(legIndex, 'destination'),
    carrierCode: getAirlineCodes(),
  };
};

const useTrackingHandler = ({
  selectedItinerary,
  alternativeItinerary,
  nonIssuable,
  pageName,
  alternativeItineraryPosition,
  valueProposition,
  valuePropositions,
  alternativeFlightType,
}) => {
  const { trackEvent } = useTracking();
  const alternativeItineraryPrice = alternativeItinerary?.price?.amount;
  const discountAmount =
    alternativeItinerary?.perks?.alternativeItineraryDiscount;
  const valuePropositionType =
    valuePropositionMapping[valueProposition] || null;
  const {
    departureAirportCode,
    destinationAirportCode,
    carrierCode: alternativeCarrierCode,
  } = useItineraryIATA(alternativeItinerary);
  const { carrierCode: selectedCarrierCode } =
    useItineraryIATA(selectedItinerary);
  const selectedItineraryPrice = selectedItinerary?.price?.amount;

  return useCallback(
    (event: string, clickedElement: string, closingMethod?: string) => {
      const trackingVer = clickedElement === 'alternative' ? 'conf' : 'card_FR';

      const buttonType = closingMethod === 'button' ? 0 : 1;
      const btn = clickedElement === 'alternative' ? `_btn:${buttonType}` : '';

      const firstValueProposition =
        valuePropositionMapping[valuePropositions[0]] || null;
      const secondValueProposition =
        valuePropositionMapping[valuePropositions[1]] || null;
      const thirdValueProposition =
        valuePropositionMapping[valuePropositions[2]] || null;

      if (alternativeItineraryPosition != null) {
        switch (event) {
          case 'onLoad':
            trackEvent({
              category: RESULTS_CATEGORY,
              action: ALTERNATIVE_FLIGHT_LAYER_ACTION,
              label: `AF_onload:${valuePropositionType}_s1:${firstValueProposition}_s2:${secondValueProposition}_s3:${thirdValueProposition}_Alt_pos:${alternativeItineraryPosition}_route:${departureAirportCode}_${destinationAirportCode}_DC:${
                discountAmount?.amount
              }${
                discountAmount?.currency
              }_pr_fr:${selectedItineraryPrice}_pr_a:${alternativeItineraryPrice}_bk:${Number(
                !nonIssuable
              )}_sa:${selectedCarrierCode}_aa:${alternativeCarrierCode}_type:${alternativeFlightType}_page:${pageName}`,
            });
            break;

          case 'discard':
            trackEvent({
              category: RESULTS_CATEGORY,
              action: ALTERNATIVE_FLIGHT_LAYER_ACTION,
              label: `AF_discard:${valuePropositionType}_s1:${firstValueProposition}_s2:${secondValueProposition}_s3:${thirdValueProposition}_Alt_pos:${alternativeItineraryPosition}_route:${departureAirportCode}_${destinationAirportCode}_DC:${
                discountAmount?.amount
              }${
                discountAmount?.currency
              }_pr_fr:${selectedItineraryPrice}_pr_a:${alternativeItineraryPrice}_disc:${closingMethod}_bk:${Number(
                !nonIssuable
              )}_sa:${selectedCarrierCode}_aa:${alternativeCarrierCode}_type:${alternativeFlightType}_page:${pageName}`,
            });
            break;

          case 'select':
            trackEvent({
              category: RESULTS_CATEGORY,
              action: ALTERNATIVE_FLIGHT_LAYER_ACTION,
              label: `AF_click_${trackingVer}:${valuePropositionType}_s1:${firstValueProposition}_s2:${secondValueProposition}_s3:${thirdValueProposition}_Alt_pos:${alternativeItineraryPosition}${btn}_route:${departureAirportCode}_${destinationAirportCode}_DC:${
                discountAmount?.amount
              }${
                discountAmount?.currency
              }_pr_fr:${selectedItineraryPrice}_pr_a:${alternativeItineraryPrice}_bk:${Number(
                !nonIssuable
              )}_sa:${selectedCarrierCode}_aa:${alternativeCarrierCode}_type:${alternativeFlightType}_page:${pageName}`,
            });
            break;
        }
      }
    },
    [
      valuePropositions,
      alternativeItineraryPosition,
      trackEvent,
      valuePropositionType,
      departureAirportCode,
      destinationAirportCode,
      discountAmount?.amount,
      discountAmount?.currency,
      selectedItineraryPrice,
      alternativeItineraryPrice,
      nonIssuable,
      selectedCarrierCode,
      alternativeCarrierCode,
      alternativeFlightType,
      pageName,
    ]
  );
};

export { useItineraryIATA, useTrackingHandler };
